'use client'

import { useEffect, useState } from 'react'
import { Eye, Users } from 'lucide-react'
import { getOrCreateVisitorId } from '@/lib/visitor-id'

async function getFingerprint(): Promise<string | null> {
  try {
    const canvas = document.createElement('canvas')
    canvas.width = 240
    canvas.height = 60
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    ctx.textBaseline = 'top'
    ctx.font = '14px Arial'
    ctx.fillStyle = '#f60'
    ctx.fillRect(2, 2, 236, 56)
    ctx.fillStyle = '#069'
    ctx.fillText('NewChirp visitor \u2713 0123456789', 8, 18)
    const rendered = canvas.toDataURL()
    const signals = [
      navigator.userAgent,
      navigator.language,
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      `${screen.width}x${screen.height}x${screen.colorDepth}`,
      navigator.platform || '',
      String(navigator.hardwareConcurrency || ''),
      typeof (navigator as Navigator & { deviceMemory?: number }).deviceMemory === 'number'
        ? String((navigator as Navigator & { deviceMemory?: number }).deviceMemory)
        : '',
      rendered,
    ].join('|')
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(signals))
    return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('')
  } catch {
    return null
  }
}

export function VisitorCounter() {
  const [totals, setTotals] = useState<{
    monthlyVisitors: number
    allTimeVisitors: number
    homePageViews: number
    homePageViewsSince: string | null
  } | null>(null)

  useEffect(() => {
    let cancelled = false
    const report = async () => {
      const id = getOrCreateVisitorId()
      const fingerprint = await getFingerprint()
      const res = await fetch('/api/visitor', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, fingerprint, path: window.location.pathname }),
      })
      const data = res.ok ? await res.json() : null
      if (
        !cancelled &&
        data &&
        typeof data.monthlyVisitors === 'number' &&
        typeof data.allTimeVisitors === 'number' &&
        typeof data.homePageViews === 'number' &&
        (typeof data.homePageViewsSince === 'string' ||
          data.homePageViewsSince === null)
      ) {
        setTotals({
          monthlyVisitors: data.monthlyVisitors,
          allTimeVisitors: data.allTimeVisitors,
          homePageViews: data.homePageViews,
          homePageViewsSince: data.homePageViewsSince,
        })
      }
    }
    report().catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  if (totals === null) return null
  const pageViewSince = totals.homePageViewsSince
    ? new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }).format(new Date(totals.homePageViewsSince))
    : 'tracking start'

  return (
    <span
      title={`${totals.monthlyVisitors.toLocaleString()} unique browsers in the last 30 days · ${totals.allTimeVisitors.toLocaleString()} all-time browser identities`}
      aria-label={`${totals.monthlyVisitors.toLocaleString()} visitors in the last 30 days and ${totals.homePageViews.toLocaleString()} non-unique home page views since ${pageViewSince}`}
      className="inline-flex min-w-0 max-w-full items-center gap-1.5 overflow-hidden whitespace-nowrap rounded-full border border-border/70 bg-background/70 px-2.5 py-1 font-mono text-[11px] text-muted-foreground sm:text-xs"
    >
      <Users className="h-3.5 w-3.5 shrink-0 text-primary" />
      <span className="shrink-0">{totals.monthlyVisitors.toLocaleString()} / 30d</span>
      <span aria-hidden className="h-3.5 w-px bg-border" />
      <Eye className="h-3.5 w-3.5 shrink-0 text-primary" />
      <span className="truncate">
        <span className="sm:hidden">{totals.homePageViews.toLocaleString()} views</span>
        <span className="hidden sm:inline">
          {totals.homePageViews.toLocaleString()} Non-unique since {pageViewSince}
        </span>
      </span>
    </span>
  )
}
